import { useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Select, type SelectProps } from '@loadsmart/loadsmart-ui'
import { Field, Icon, Layout, Text, TextField } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { type AgnosticShipper } from 'core/domain/AgnosticShipper'
import { type Mode } from 'core/domain/Mode'
import { filterOptions, mapToOptions, type Option } from 'core/domain/Option'
import { getStopFromLocation, type Stop } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { mapStopToString } from 'core/infra/StopMapper'
import { mirandaSizes } from 'core/miranda-styles/screen'
import { formatStopToOption } from 'core/presenters/stopPresenter'
import { DateSelect } from 'core/ui/components/date/DateSelect'
import {
  FilterSummary,
  type FilterSummaryItem,
} from 'core/ui/components/filter-summary/FilterSummary'
import { ModeSelect } from 'core/ui/components/mode/ModeSelect'
import { SearchableSelect } from 'core/ui/components/select/SearchableSelect'
import { TruckSelect } from 'core/ui/components/truck/TruckSelect'
import { toArray } from 'core/utils/array'
import { isEmpty, sortAlphabetically } from 'core/utils/string'

import useQueryParams from 'legacy/utils/hooks/useQueryParams'

import { useConfigContext } from 'config/ui/ConfigContext'

import { DEFAULT_SHIPMENT_IDENTIFIERS } from '../domain/AgnosticShipment'
import {
  type DerivedShipmentStatus,
  type AgnosticShipmentFilter,
} from '../domain/AgnosticShipmentFilter'
import {
  formatEquipment,
  formatLane,
  formatMode,
  formatPeriod,
  formatShipper,
  formatStatus,
} from '../presenters/agnosticShipmentFilterPresenter'
import { formatShipmentIdentifier } from '../presenters/agnosticShipmentPresenter'
import { useAgnosticShippers } from './hooks/useAgnosticShippers'
import { PickupAndDelivery } from './PickupAndDelivery'
import { StatusToggle } from './ShipmentsList/StatusToggle'
import { type SearchFilters } from './ShipperLoadsPage'

const SHIPMENT_IDENTIFIER_OPTIONS = mapToOptions({
  values: DEFAULT_SHIPMENT_IDENTIFIERS,
  getValue: value => value,
  getLabel: formatShipmentIdentifier,
  sort: false,
})

const FilterWrapper = styled(Layout.Stack)`
  background-color: rgba(${getToken('color-background-primary')});
  margin: -${getToken('spacing-6')};
  padding: ${getToken('spacing-6')};
  gap: ${getToken('spacing-6')};

  @media (max-width: ${mirandaSizes.sm}) {
    padding: ${getToken('spacing-4')};
    gap: ${getToken('spacing-4')};
    margin: -${getToken('spacing-4')};
  }
`

const FilterFirstRow = styled(Layout.Group)`
  width: 100%;
  flex-flow: row;

  @media (max-width: ${mirandaSizes.sm}) {
    flex-flow: column;
  }
`

const FilterSecondRow = styled(Layout.Group)`
  width: 100%;
  flex-wrap: nowrap;

  > m-field {
    min-width: 210px;
    align-self: flex-end;
  }

  @media (max-width: ${mirandaSizes.sm}) {
    flex-flow: column-reverse;

    > m-field {
      align-self: unset;
    }
  }
`

const ShipperModeEquipmentWrapper = styled.div`
  display: flex;
  flex-flow: row;
  gap: ${getToken('spacing-4')};
  width: 100%;

  @media (max-width: ${mirandaSizes.sm}) {
    > m-field,
    > m-stack {
      max-width: 103px;
    }

    m-stack {
      flex: none;
    }
  }
`

function buildShipmentNumberSummary(filterName: string, filterValue = '') {
  // Split the filter values into an array
  const filterValuesArray = String(filterValue).split(',')

  // Map each filter value to a FilterSummaryItem
  return filterValuesArray.map(value => ({
    id: `${filterName.toLowerCase()}-${value}`,
    label: filterName,
    value,
  }))
}

function buildFilterSummaryItems(
  filter: AgnosticShipmentFilter,
  selectedShipperOptions: Array<Option<number>>
): Array<FilterSummaryItem> {
  const bolValues = buildShipmentNumberSummary('BOL', filter.bol)
  const poValues = buildShipmentNumberSummary('PO', filter.po)
  const referenceValues = buildShipmentNumberSummary('REF', filter.reference)
  const soValues = buildShipmentNumberSummary('SO', filter.so)

  return [
    { id: 'lane', label: 'Lane', value: formatLane(filter) },
    { id: 'period', label: 'Period', value: formatPeriod(filter) },
    { id: 'status', label: 'Status', value: formatStatus(filter) },
    { id: 'shipper', label: 'Shipper', value: formatShipper(selectedShipperOptions) },
    { id: 'mode', label: 'Mode', value: formatMode(filter) },
    { id: 'equipment', label: 'Equipment', value: formatEquipment(filter) },
  ]
    .concat(bolValues, poValues, referenceValues, soValues)
    .filter(({ value }) => !isEmpty(value))
}

type SelectOnChangeEvent = Parameters<NonNullable<SelectProps['onChange']>>[0]

export type ShipperLoadsFilterProps = {
  filters: AgnosticShipmentFilter
  onChange?: (filters: SearchFilters | undefined) => void
  onSearchChange?: (searchQuery: string) => void
  searchQuery?: string
}

export function ShipperLoadsFilter({
  filters,
  onChange,
  onSearchChange,
  searchQuery,
}: ShipperLoadsFilterProps) {
  const { pickup, delivery, pickupDateAfter, pickupDateBefore, status, mode, equipment, shipper } =
    filters

  const { shippers, isFetchingShippers, hasFetchShippersError, fetchShippers } =
    useAgnosticShippers()

  const queryParams = useQueryParams()

  const enableShipmentNumberFilter = useConfigContext().enableShipmentNumberFilter.toBoolean()
  const enableShipmentGlobalSearch = useConfigContext().enableShipmentGlobalSearch.toBoolean()

  useEffect(() => {
    fetchShippers()
  }, [fetchShippers])

  useEffect(() => {
    if (hasFetchShippersError) {
      toast.error('Failed to get shippers for the filter. Please reload the page to try again.')
    }
  }, [hasFetchShippersError])

  const shipperOptions = useMemo(
    () =>
      mapToOptions({
        values: shippers,
        getValue: (shipper: AgnosticShipper) => shipper.id,
        getLabel: (shipper: AgnosticShipper) => shipper.companyName,
      }),
    [shippers]
  )

  const shipperValue = filterOptions(shipperOptions, shipper)
  const filterSummaryItems = buildFilterSummaryItems(filters, shipperValue)

  function handleChangeLocation(newValue: { pickup?: Stop; delivery?: Stop }): void {
    const { pickup, delivery } = newValue

    const puStop = mapStopToString(pickup)
    const delStop = mapStopToString(delivery)

    onChange?.({ pickup: puStop, delivery: delStop })
  }

  function handleChangeDate(newValue: { startDate?: string; endDate?: string }): void {
    const { startDate, endDate } = newValue

    onChange?.({
      pickupDateAfter: startDate,
      pickupDateBefore: endDate,
    })
  }

  function handleChangeStatus(value: DerivedShipmentStatus[]) {
    onChange?.({
      status: value,
    })
  }

  function handleChangeSelect(field: 'status' | 'shipper', event: SelectOnChangeEvent): void {
    onChange?.({
      [field]: toArray(event.target.value)
        .map(option => (option as Option).value)
        .sort(),
    })
  }

  function handleModeSelect(newValue: Array<Mode> = []): void {
    const sorted = [...newValue].sort(sortAlphabetically)
    onChange?.({ mode: sorted })
  }

  function handleTruckSelect(newValue: Array<TruckType> = []) {
    const sorted = [...newValue].sort(sortAlphabetically)
    onChange?.({ equipment: sorted })
  }

  function handleShipmentNumberSearch(newSelect: Option, newSearch: string) {
    if (isEmpty(newSearch)) return

    // Get the current shipment value select from the selected option
    const currentShipmentIdentifier = newSelect.value as string

    // Get the current search value for the shipment number from the query params
    const currentShipmentSearch = queryParams[currentShipmentIdentifier]

    // Convert the current search value to an array
    let currentShipmentSearchArray = String(currentShipmentSearch).split(',')

    // Check if the new search is already in the array
    if (currentShipmentSearchArray.includes(newSearch)) return

    // Add the new search to the array
    currentShipmentSearchArray.push(newSearch)

    onChange?.({
      [currentShipmentIdentifier]: currentShipmentSearch
        ? currentShipmentSearchArray.toString()
        : newSearch,
    })
  }

  function handleClearFilter(): void {
    onChange?.(undefined)
  }

  function clearShipmentNumber(shipmentNumber: string, id: string) {
    // Find the filter item with the specified id
    const filterItem = filterSummaryItems.find(item => item.id === id)

    // Get the current search value for the shipment number from the query params
    const currentShipSearch = queryParams[shipmentNumber] ?? ''

    // Convert the current search value to an array
    let currentShipSearchArray = String(currentShipSearch).split(',')

    // Find the index of the filter item's value in the array
    const indexToRemove = currentShipSearchArray.indexOf(filterItem!.value)

    // Remove the item at the found index from the array
    currentShipSearchArray.splice(indexToRemove, 1)

    onChange?.({
      [shipmentNumber]: currentShipSearchArray.toString(),
    })
  }

  function handleClearFilterItem(id: string): void {
    if (id === 'lane') {
      onChange?.({
        pickup: undefined,
        delivery: undefined,
      })
    } else if (id === 'period') {
      onChange?.({
        pickupDateAfter: undefined,
        pickupDateBefore: undefined,
      })
    } else if (id.includes('bol')) {
      clearShipmentNumber('bol', id)
    } else if (id.includes('po')) {
      clearShipmentNumber('po', id)
    } else if (id.includes('ref')) {
      clearShipmentNumber('reference', id)
    } else if (id.includes('so')) {
      clearShipmentNumber('so', id)
    } else {
      onChange?.({ [id]: undefined })
    }
  }

  function handleShipperSelectionError() {
    if (hasFetchShippersError) fetchShippers()
  }

  return (
    <FilterWrapper>
      <FilterFirstRow>
        <PickupAndDelivery
          pickupValue={pickup}
          deliveryValue={delivery}
          pickupLabel="Pickup"
          deliveryLabel="Delivery"
          style={{ flexFlow: 'row' }}
          mapLocationToValue={getStopFromLocation}
          mapValueToOption={formatStopToOption}
          onChange={handleChangeLocation}
        />
        <DateSelect
          name="date"
          label="Date"
          startDateValue={pickupDateAfter}
          endDateValue={pickupDateBefore}
          dateFormat="yyyy-MM-dd"
          onChange={handleChangeDate}
        />
        <StatusToggle id="status" onChange={handleChangeStatus} value={status} />
      </FilterFirstRow>
      <FilterSecondRow>
        {enableShipmentNumberFilter && !enableShipmentGlobalSearch && (
          <SearchableSelect
            clearAfterSearch
            label="Shipment number"
            options={SHIPMENT_IDENTIFIER_OPTIONS}
            name="shipment-number"
            searchPlaceholder="Search"
            onChange={handleShipmentNumberSearch}
          />
        )}
        {enableShipmentGlobalSearch && (
          <Field>
            <TextField
              aria-label="search"
              placeholder="Search by ID or reference"
              value={searchQuery}
              leading={<Icon name="search" size="16px" />}
              onInput={({ target }) => {
                const { value } = target as HTMLInputElement

                onSearchChange?.(value)
              }}
              maxLength={500}
            />
          </Field>
        )}
        <ShipperModeEquipmentWrapper>
          <Layout.Stack gap="spacing-1" style={{ flex: 1 }}>
            <Field>
              <Field.Label id="shipper-select-label">Shipper</Field.Label>
              <Select
                id="shipper"
                name="shipper"
                placeholder="Shipper"
                options={shipperOptions}
                value={shipperValue}
                onChange={event => handleChangeSelect('shipper', event)}
                onClick={handleShipperSelectionError}
                disabled={isFetchingShippers}
                multiple
                aria-labelledby="shipper-select-label"
              />
            </Field>
            {hasFetchShippersError && (
              <Text variant="body-sm" color="color-text-error">
                Failed to load Shippers. Please reload the page to try again.
              </Text>
            )}
          </Layout.Stack>
          <ModeSelect
            label="Mode"
            placeholder="Modes"
            name="mode"
            value={mode}
            onChange={handleModeSelect}
            style={{ flex: 1 }}
            multiple
          />
          <TruckSelect
            label="Equipment"
            name="equipment"
            id="equipment"
            placeholder="Equipment"
            value={equipment}
            style={{ flex: 1 }}
            onChange={handleTruckSelect}
            multiple
          />
        </ShipperModeEquipmentWrapper>
      </FilterSecondRow>
      <FilterSummary
        items={filterSummaryItems}
        onClearAll={handleClearFilter}
        onClearItem={handleClearFilterItem}
      />
    </FilterWrapper>
  )
}
